import { useEffect, useRef, useState } from "react";
import { Globe, Loader2, Sparkles, Check, X, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2, 12);

// Import services from a business website URL → OperaMind crawls + extracts →
// review structured services → merge into the editor via onAdd(services).
// Reuses the /import/website pipeline but ONLY applies services (no full-profile overwrite).
export function ServiceWebsiteImport({ onAdd }) {
  const [url, setUrl] = useState("");
  const [running, setRunning] = useState(false);
  const [job, setJob] = useState(null);
  const [review, setReview] = useState([]);
  const poll = useRef(null);

  useEffect(() => () => clearInterval(poll.current), []);

  const start = async () => {
    if (!url.trim()) { toast.error("Please enter your website URL"); return; }
    setRunning(true);
    setReview([]);
    setJob({ stage: "Starting…", progress: 2 });
    try {
      const r = await api.post("/import/website", { url: url.trim() });
      poll.current = setInterval(async () => {
        try {
          const s = await api.get(`/import/website/${r.data.job_id}`);
          setJob(s.data);
          if (s.data.status === "done") {
            clearInterval(poll.current);
            setRunning(false);
            const svcs = (s.data.result?.services || []).map((x) => ({ ...x, _include: true, _rk: uid() }));
            setReview(svcs);
            toast[svcs.length ? "success" : "info"](
              svcs.length ? `Found ${svcs.length} service${svcs.length === 1 ? "" : "s"} — review below before adding.`
                          : "No services detected on that site — try a page with your prices, or add them manually.");
          } else if (s.data.status === "error") {
            clearInterval(poll.current);
            setRunning(false);
            toast.error(s.data.error || "Import failed");
            setJob(null);
          }
        } catch { /* keep polling */ }
      }, 2500);
    } catch (e) {
      setRunning(false); setJob(null);
      toast.error(e.response?.data?.detail || "Could not start import");
    }
  };

  const setField = (rk, patch) => setReview((prev) => prev.map((s) => (s._rk === rk ? { ...s, ...patch } : s)));
  const removeRow = (rk) => setReview((prev) => prev.filter((s) => s._rk !== rk));

  const approve = () => {
    const chosen = review.filter((s) => s._include && (s.name || "").trim())
      .map(({ _include, _rk, ...s }) => ({ ...s, id: s.id || uid(), enabled: true }));
    if (!chosen.length) { toast.error("Select at least one service to add."); return; }
    onAdd?.(chosen);
    setReview([]);
    toast.success(`${chosen.length} service${chosen.length === 1 ? "" : "s"} added — edit or save them above.`);
  };

  const pct = job?.progress || 5;

  return (
    <div data-testid="service-website-import">
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          data-testid="svc-web-url-input"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !running && start()}
          placeholder="https://www.mybusiness.co.uk"
          disabled={running}
        />
        <Button data-testid="svc-web-import-btn" onClick={start} disabled={running} className="bg-blue-600 hover:bg-blue-700 gap-1.5 shrink-0">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />} Import services
        </Button>
      </div>

      {running && (
        <div data-testid="svc-web-progress" className="mt-3">
          <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-blue-600 transition-all duration-500" style={{ width: `${pct}%` }} /></div>
          <p className="mt-2 text-xs text-slate-500">{job?.stage || "Analysing your website…"} · {pct}%</p>
        </div>
      )}

      {review.length > 0 && (
        <div data-testid="svc-web-review" className="mt-4 rounded-2xl border border-blue-200 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="font-semibold text-slate-900 flex items-center gap-2"><Sparkles className="h-4 w-4 text-blue-600" /> Review extracted services ({review.filter((s) => s._include).length} selected)</p>
            <div className="flex gap-2">
              <Button data-testid="svc-web-review-cancel" variant="ghost" size="sm" onClick={() => setReview([])} className="text-slate-500 gap-1"><X className="h-4 w-4" /> Discard</Button>
              <Button data-testid="svc-web-review-approve" size="sm" onClick={approve} className="bg-blue-600 hover:bg-blue-700 gap-1"><Check className="h-4 w-4" /> Add selected</Button>
            </div>
          </div>
          <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
            {review.map((s, i) => (
              <div key={s._rk} data-testid={`svc-web-review-row-${i}`} className={`flex items-start gap-2 rounded-lg border bg-white p-2.5 ${s._include ? "border-slate-200" : "border-slate-200 opacity-50"}`}>
                <Checkbox data-testid={`svc-web-review-include-${i}`} checked={s._include} onCheckedChange={(v) => setField(s._rk, { _include: !!v })} className="mt-2" />
                <div className="flex-1 grid sm:grid-cols-12 gap-2">
                  <Input data-testid={`svc-web-review-name-${i}`} value={s.name || ""} onChange={(e) => setField(s._rk, { name: e.target.value })} placeholder="Service name" className="sm:col-span-4 h-9 font-medium" />
                  <Input data-testid={`svc-web-review-price-${i}`} value={s.price || ""} onChange={(e) => setField(s._rk, { price: e.target.value })} placeholder="Price" className="sm:col-span-2 h-9" />
                  <Input value={s.category || ""} onChange={(e) => setField(s._rk, { category: e.target.value })} placeholder="Category" className="sm:col-span-3 h-9" />
                  <Input value={s.description || ""} onChange={(e) => setField(s._rk, { description: e.target.value })} placeholder="Description" className="sm:col-span-3 h-9" />
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeRow(s._rk)} className="text-slate-400 hover:text-red-500 h-8 w-8 shrink-0"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ServiceWebsiteImport;
